// QuickBooks in Base Camp (PRD rev. 1.22, QB1–QB8): connecting, syncing, mapping accounts to Base
// Camp's categories and customers to clients, editing the categories, and assigning the lines
// QuickBooks leaves without a client or category. Admins only (books.read, SEC20).

import { useMemo, useState } from "react";
import { Badge, Body1, Button, Caption1, Card, Field, Input, MessageBar, MessageBarBody, Radio, RadioGroup, Select, Subtitle2, Switch, Tab, TabList } from "@fluentui/react-components";
import { UNMAPPED, type BooksAccount, type CodingProject, type CostTreatment, type ResolvedLine, type SpendCategory } from "../../../core";
import { errorText, send, useApi } from "../api";
import { day, money, when } from "../format";
import { go } from "../router";
import { ErrorBar, Loading, Page } from "../ui";
import { AccountCoding, CodeCosts } from "./CostCoding";

/** Intuit sends the admin back here; App keeps its answer before sign-in can touch the address. */
export const CALLBACK_PATH = "/quickbooks-callback";
export const CALLBACK_KEY = "qbo-callback";
const redirectUri = () => `${window.location.origin}${CALLBACK_PATH}`;

interface StatusResponse {
  available: boolean;
  environment: "production" | "sandbox";
  clientId: string | null;
  sandboxClientId: string | null;
  secretStored: boolean;
  connected: boolean;
  companyName: string | null;
  connectedBy: string | null;
  connectedAt: string | null;
  refreshExpiresAt: string | null;
  lastSync: {
    at: string;
    ok: boolean;
    from?: string;
    to?: string;
    lines?: number;
    error?: string;
    intuitTid?: string;
    by: string;
    shape?: { columns: string[]; sections: number; dataRows: number; groups: string[]; firstRow: string[]; found: Record<string, number> };
  } | null;
  synced: string[];
  usage: { calls: number; cap: number; allowance: number };
  historyFrom: string;
  thisMonth: string;
  unmappedAccounts: number;
  openLines: number;
  unmatchedCustomers: number;
}
type MappedAccount = BooksAccount & { categoryId: string; own: string | null; suggested: string; last12: number; kind: "income" | "expense"; coding: { code?: string; treatment: CostTreatment } | null };
interface MappingResponse {
  etag: string | null;
  categories: SpendCategory[];
  accounts: MappedAccount[];
  customers: { id: string; name: string; client: string; matched: boolean; override: boolean; income: number }[];
  knownClients: string[];
  projects: CodingProject[];
}
interface AssignResponse {
  open: ResolvedLine[];
  assigned: ResolvedLine[];
  categories: SpendCategory[];
  knownClients: string[];
}

const TABS = [
  { key: "connection", label: "Connection" },
  { key: "accounts", label: "Accounts" },
  { key: "customers", label: "Customers" },
  { key: "categories", label: "Categories" },
  { key: "assign", label: "Assign" },
  { key: "coding", label: "Code costs" },
] as const;

export function Books({ tab }: { tab?: string }) {
  const status = useApi<StatusResponse>("/books/status");
  const current = TABS.find((t) => t.key === tab) ?? TABS[0];
  const s = status.data;
  const count = (k: string) => (k === "accounts" ? s?.unmappedAccounts : k === "customers" ? s?.unmatchedCustomers : k === "assign" ? s?.openLines : 0) ?? 0;
  return (
    <Page title="QuickBooks">
      <TabList selectedValue={current.key} onTabSelect={(_, d) => go("books", String(d.value))}>
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key}>
            {t.label}
            {count(t.key) > 0 && (
              <Badge size="small" appearance="tint" color="warning" className="tab-count">
                {count(t.key)}
              </Badge>
            )}
          </Tab>
        ))}
      </TabList>
      <ErrorBar message={status.error} />
      {!s ? (
        <Loading />
      ) : current.key === "connection" ? (
        <Connection status={s} reload={status.reload} />
      ) : current.key === "assign" ? (
        <Assign onChange={status.reload} />
      ) : current.key === "coding" ? (
        <CodeCosts onChange={status.reload} />
      ) : (
        <Mapping tab={current.key} onChange={status.reload} />
      )}
    </Page>
  );
}

// --- Connection (QB1, QB2, QB7) ---------------------------------------------------------------------------

function Connection({ status, reload }: { status: StatusResponse; reload: () => void }) {
  const [env, setEnv] = useState(status.environment);
  const savedId = (env === "sandbox" ? status.sandboxClientId : status.clientId) ?? "";
  const [clientId, setClientId] = useState(savedId);
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [progress, setProgress] = useState<string>();
  const run = async (label: string, f: () => Promise<void>) => {
    setBusy(label);
    setError(undefined);
    try {
      await f();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(undefined);
      setProgress(undefined);
      reload();
    }
  };
  if (!status.available) {
    return (
      <MessageBar intent="info">
        <MessageBarBody>QuickBooks isn't switched on yet: it needs its Key Vault, which an infrastructure run creates.</MessageBarBody>
      </MessageBar>
    );
  }
  // History from 2025-01 to this month, read 12 months at a time so each request stays short (QB2).
  const missing = (() => {
    const out: string[] = [];
    for (let m = status.historyFrom; m <= status.thisMonth; ) {
      if (!status.synced.includes(m)) out.push(m);
      const [y, mo] = m.split("-").map(Number);
      m = mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, "0")}`;
    }
    return out;
  })();
  const everyMonth = (() => {
    const out: string[] = [];
    for (let m = status.historyFrom; m <= status.thisMonth; ) {
      out.push(m);
      const [y, mo] = m.split("-").map(Number);
      m = mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, "0")}`;
    }
    return out;
  })();
  const readHistory = (months: string[] = missing) =>
    run("history", async () => {
      for (let i = 0; i < months.length; i += 12) {
        const chunk = months.slice(i, i + 12);
        setProgress(`Reading ${chunk[0]} to ${chunk.at(-1)}…`);
        const r = await send<{ result: { ok: boolean; error?: string } }>("POST", "/books/sync", { from: chunk[0], to: chunk.at(-1) });
        if (!r.result.ok) throw new Error(r.result.error ?? "The sync stopped.");
      }
    });
  const lapses = status.refreshExpiresAt ? Math.floor((Date.parse(status.refreshExpiresAt) - Date.now()) / 86_400_000) : null;
  return (
    <>
      <ErrorBar message={error} />
      <Card className="panel">
        <div className="panel-head">
          <Subtitle2>The Intuit app</Subtitle2>
          {status.environment === "sandbox" && (
            <Badge appearance="filled" color="warning">
              Sandbox: test data
            </Badge>
          )}
        </div>
        <Caption1>
          Created once at developer.intuit.com with the 3Seven sign-in. Its redirect address must be <code>{redirectUri()}</code> for each set of keys. A client secret goes straight to Key Vault and is never shown again.
        </Caption1>
        <RadioGroup
          layout="horizontal"
          value={env}
          disabled={status.connected}
          onChange={(_, d) => {
            const next = d.value as "production" | "sandbox";
            setEnv(next);
            setClientId((next === "sandbox" ? status.sandboxClientId : status.clientId) ?? "");
            setSecret("");
          }}
        >
          <Radio value="production" label="Production: 3Seven's QuickBooks, with production keys" />
          <Radio value="sandbox" label="Sandbox: an Intuit test company, with development keys" />
        </RadioGroup>
        {status.connected && <Caption1 className="muted">Disconnect to switch between production and the sandbox. Connecting a different company clears the figures read from the last one.</Caption1>}
        <div className="form-grid three">
          <Field label="Client id">
            <Input value={clientId} onChange={(_, d) => setClientId(d.value.trim())} />
          </Field>
          <Field label="Client secret" hint={status.secretStored && env === status.environment ? "Stored in Key Vault. Paste a new one only to replace it." : "Not stored yet for these keys."}>
            <Input type="password" autoComplete="off" value={secret} onChange={(_, d) => setSecret(d.value)} />
          </Field>
        </div>
        <div className="row-actions">
          <Button
            appearance="primary"
            disabled={!!busy || !clientId || (clientId === savedId && env === status.environment && !secret)}
            onClick={() =>
              void run("app", async () => {
                await send("PUT", "/books/app", { environment: env, clientId, clientSecret: secret || undefined });
                setSecret("");
              })
            }
          >
            {busy === "app" ? "Saving…" : "Save"}
          </Button>
        </div>
      </Card>

      <Card className="panel">
        <div className="panel-head">
          <Subtitle2>Connection</Subtitle2>
          {status.connected ? (
            <Badge appearance="tint" color="success">
              Connected
            </Badge>
          ) : (
            <Badge appearance="tint" color="informative">
              Not connected
            </Badge>
          )}
        </div>
        {status.connected ? (
          <>
            <Body1>
              <strong>{status.companyName || "QuickBooks company"}</strong>, connected by {status.connectedBy} {status.connectedAt ? `on ${when(status.connectedAt)}` : ""}.
              {lapses !== null && ` The connection lapses ${lapses < 0 ? "already" : `in ${lapses} days`} unless it syncs; each sync renews it.`}
            </Body1>
            <Body1>
              {status.synced.length ? `${status.synced.length} months read, ${status.synced[0]} to ${status.synced.at(-1)}.` : "Nothing read yet."}{" "}
              {status.lastSync &&
                (status.lastSync.ok
                  ? `Last sync ${when(status.lastSync.at)} by ${status.lastSync.by}: ${status.lastSync.from} to ${status.lastSync.to}, ${status.lastSync.lines} lines.`
                  : `The last sync failed ${when(status.lastSync.at)}: ${status.lastSync.error}`)}
              {status.lastSync?.ok && status.lastSync.intuitTid && <Caption1 className="block muted">Intuit reference for support: {status.lastSync.intuitTid}</Caption1>}
              {status.lastSync?.ok && status.lastSync.lines === 0 && status.lastSync.shape && (
                <MessageBar intent="warning" layout="multiline">
                  <MessageBarBody>
                    The last sync read QuickBooks's reports but found no lines in them. Their layout, without any figures or names, for Base Camp's support:
                    <code className="block">
                      {`columns: ${status.lastSync.shape.columns.join(", ")}`}
                      <br />
                      {`sections ${status.lastSync.shape.sections}, data rows ${status.lastSync.shape.dataRows}, groups: ${status.lastSync.shape.groups.join(", ") || "none"}`}
                      <br />
                      {`first row: ${status.lastSync.shape.firstRow.join(", ") || "none"}; found ${Object.entries(status.lastSync.shape.found).map(([k, v]) => `${k}@${v}`).join(" ")}`}
                    </code>
                  </MessageBarBody>
                </MessageBar>
              )}
            </Body1>
            {progress && <Caption1>{progress}</Caption1>}
            <div className="row-actions">
              {missing.length > 0 && (
                <Button appearance="primary" disabled={!!busy} onClick={() => void readHistory()}>
                  {busy === "history" ? "Reading…" : `Read history (${missing.length} months)`}
                </Button>
              )}
              {missing.length === 0 && status.synced.length > 0 && (
                <Button
                  disabled={!!busy}
                  onClick={() =>
                    window.confirm(`Read all ${everyMonth.length} months again? About ${3 * Math.ceil(everyMonth.length / 12) + everyMonth.length * 3} QuickBooks calls, well inside the monthly cap. Use it after changing past months in QuickBooks, or to refresh the totals Reports checks against.`) &&
                    void readHistory(everyMonth)
                  }
                >
                  {busy === "history" ? "Reading…" : "Read all history again"}
                </Button>
              )}
              <Button disabled={!!busy} onClick={() => void run("sync", async () => void (await send("POST", "/books/sync", {})))}>
                {busy === "sync" ? "Syncing…" : "Sync recent months"}
              </Button>
              <Button
                appearance="subtle"
                disabled={!!busy}
                onClick={() => window.confirm("Disconnect QuickBooks? The figures already read stay; nothing new is read until it's connected again.") && void run("off", async () => void (await send("POST", "/books/disconnect", {})))}
              >
                Disconnect
              </Button>
            </div>
            <Caption1>
              Each night Base Camp re-reads this month and the two before, since entries still land there. It only ever reads QuickBooks. This month: {status.usage.calls.toLocaleString("en-US")} QuickBooks calls, capped at{" "}
              {status.usage.cap.toLocaleString("en-US")} to stay well inside the free plan's {status.usage.allowance.toLocaleString("en-US")}.
            </Caption1>
          </>
        ) : (
          <>
            <Body1>Connecting opens Intuit's sign-in. Pick the 3Seven company there; Intuit sends you back here.</Body1>
            <div className="row-actions">
              <Button
                appearance="primary"
                disabled={!!busy || !(status.environment === "sandbox" ? status.sandboxClientId : status.clientId) || !status.secretStored}
                onClick={() =>
                  void run("connect", async () => {
                    const r = await send<{ url: string }>("POST", "/books/connect", { redirectUri: redirectUri() });
                    const to = new URL(r.url);
                    // Intuit is always another site. Only the mock's stand-in answers on this one,
                    // and a reload would wipe its in-memory data, so it finishes here.
                    if (to.origin !== window.location.origin) return window.location.assign(r.url);
                    const failed = await finishCallback(to.search);
                    if (failed) throw new Error(failed);
                  })
                }
              >
                Connect QuickBooks
              </Button>
            </div>
          </>
        )}
      </Card>
    </>
  );
}

/** Finishes connecting with what Intuit sent back (QB1); App kept it in the session for this. */
export async function finishCallback(search: string): Promise<string | undefined> {
  const q = new URLSearchParams(search);
  if (q.get("error")) return q.get("error") === "access_denied" ? "Connecting was cancelled at Intuit." : "Intuit didn't connect QuickBooks.";
  try {
    await send("POST", "/books/callback", { code: q.get("code"), state: q.get("state"), realmId: q.get("realmId"), redirectUri: redirectUri() });
    return undefined;
  } catch (err) {
    return errorText(err);
  }
}

// --- Accounts, customers and categories (QB4, QB5) -----------------------------------------------------------

function categoryLabel(id: string, categories: readonly SpendCategory[]) {
  const c = categories.find((x) => x.id === id);
  if (!c) return id;
  const parent = c.parentId ? categories.find((x) => x.id === c.parentId) : undefined;
  return parent ? `${parent.name} › ${c.name}` : c.name;
}

function CategorySelect(props: { value: string; categories: readonly SpendCategory[]; onChange: (v: string) => void; inherit?: string; size?: "small" | "medium"; exclude?: string }) {
  const ordered = props.categories.filter((c) => !c.parentId).flatMap((top) => [top, ...props.categories.filter((c) => c.parentId === top.id)]);
  return (
    <Select size={props.size ?? "small"} value={props.value} aria-label="Category" onChange={(_, d) => props.onChange(d.value)}>
      {props.inherit !== undefined && <option value="">{props.inherit}</option>}
      {ordered
        .filter((c) => c.id !== props.exclude)
        .map((c) => (
          <option key={c.id} value={c.id}>
            {categoryLabel(c.id, props.categories)}
          </option>
        ))}
    </Select>
  );
}

function Mapping({ tab, onChange }: { tab: "accounts" | "customers" | "categories"; onChange: () => void }) {
  const data = useApi<MappingResponse>("/books/mapping");
  const d = data.data;
  if (!d) return data.error ? <ErrorBar message={data.error} /> : <Loading />;
  const reload = () => (data.reload(), onChange());
  return tab === "categories" ? <Categories data={d} reload={reload} /> : <MappingTables key={d.etag ?? "new"} tab={tab} data={d} reload={reload} />;
}

function MappingTables({ tab, data, reload }: { tab: "accounts" | "customers"; data: MappingResponse; reload: () => void }) {
  const initialAccounts = useMemo(() => Object.fromEntries(data.accounts.filter((a) => a.own).map((a) => [a.id, a.own!])), [data]);
  const initialCustomers = useMemo(() => Object.fromEntries(data.customers.filter((c) => c.override).map((c) => [c.id, c.client])), [data]);
  const [accounts, setAccounts] = useState<Record<string, string>>(initialAccounts);
  const [customers, setCustomers] = useState<Record<string, string>>(initialCustomers);
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const dirty = JSON.stringify(accounts) !== JSON.stringify(initialAccounts) || JSON.stringify(customers) !== JSON.stringify(initialCustomers);
  const byId = new Map(data.accounts.map((a) => [a.id, a]));
  const resolved = (a: MappedAccount): string => {
    for (let x: MappedAccount | undefined = a, i = 0; x && i < 6; x = x.parentId ? byId.get(x.parentId) : undefined, i++) if (accounts[x.id]) return accounts[x.id];
    return UNMAPPED;
  };
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/books/mapping", { etag: data.etag, accounts, customers });
      reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const spend = data.accounts.filter((a) => a.kind === "expense");
  const rows = spend.filter((a) => !onlyOpen || resolved(a) === UNMAPPED);
  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>{tab === "accounts" ? "QuickBooks accounts and their categories" : "QuickBooks customers and their clients"}</Subtitle2>
        <div className="row-actions">
          {tab === "accounts" && <Switch label="Unmapped only" checked={onlyOpen} onChange={(_, v) => setOnlyOpen(v.checked)} />}
          <Button appearance="primary" size="small" disabled={!dirty || busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
      <ErrorBar message={error} />
      {tab === "accounts" ? (
        <>
          <Caption1>
            Every spending account counts in one of Base Camp's categories. A sub-account takes its parent's unless it's set itself. A new QuickBooks account lands in Unmapped until it's set here. A change applies to every month. A default
            project (an event account for its client's project, say) codes every line in the account, saved as soon as it's picked; a line's own coding on the Code costs tab wins over it.
          </Caption1>
          {rows.length === 0 ? (
            <Body1 className="muted">{spend.length ? "Every account has a category." : "No accounts read yet: sync QuickBooks first."}</Body1>
          ) : (
            <div className="table-scroll">
              <table className="edit-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th className="num">Last 12 months</th>
                    <th>Category</th>
                    <th>Default project</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => {
                    const parent = a.parentId ? byId.get(a.parentId) : undefined;
                    const inherited = parent ? categoryLabel(resolved(parent), data.categories) : undefined;
                    const value = accounts[a.id] ?? (parent ? "" : UNMAPPED);
                    return (
                      <tr key={a.id}>
                        <td>
                          {a.fullName}
                          {!a.active && <Caption1 className="block muted">Inactive in QuickBooks</Caption1>}
                          {resolved(a) === UNMAPPED && a.suggested !== UNMAPPED && <Caption1 className="block muted">Suggested: {categoryLabel(a.suggested, data.categories)}</Caption1>}
                        </td>
                        <td className="num">{money(a.last12)}</td>
                        <td>
                          <CategorySelect
                            value={value}
                            categories={data.categories}
                            inherit={parent ? `Same as ${parent.name} (${inherited})` : undefined}
                            onChange={(v) => {
                              const next = { ...accounts };
                              if (v) next[a.id] = v;
                              else delete next[a.id];
                              setAccounts(next);
                            }}
                          />
                        </td>
                        <td>
                          <AccountCoding accountId={a.id} coding={a.coding} projects={data.projects} onSaved={reload} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <>
          <Caption1>Customers become clients by name, the way projects find theirs. Type a client's name to join a customer to one whose name differs; leave it empty to keep the customer's own.</Caption1>
          {data.customers.length === 0 ? (
            <Body1 className="muted">No customers read yet: sync QuickBooks first.</Body1>
          ) : (
            <div className="table-scroll">
              <table className="edit-table">
                <thead>
                  <tr>
                    <th>QuickBooks customer</th>
                    <th className="num">Invoiced (synced months)</th>
                    <th>Base Camp client</th>
                  </tr>
                </thead>
                <tbody>
                  {data.customers.map((c) => (
                    <tr key={c.id}>
                      <td>
                        {c.name}
                        {!c.matched && !customers[c.id] && <Caption1 className="block muted">No Base Camp client by this name: it counts as its own.</Caption1>}
                      </td>
                      <td className="num">{money(c.income)}</td>
                      <td>
                        <Input
                          size="small"
                          list="books-clients"
                          value={customers[c.id] ?? ""}
                          placeholder={c.override ? "" : c.client}
                          onChange={(_, v) => {
                            const next = { ...customers };
                            if (v.value.trim()) next[c.id] = v.value;
                            else delete next[c.id];
                            setCustomers(next);
                          }}
                          aria-label={`Client for ${c.name}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <datalist id="books-clients">
                {data.knownClients.map((k) => (
                  <option key={k} value={k} />
                ))}
              </datalist>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function Categories({ data, reload }: { data: MappingResponse; reload: () => void }) {
  const [rows, setRows] = useState<SpendCategory[]>(data.categories);
  const shared = useApi<{ doc: { categories: { id: string; name: string; active: boolean }[] } }>("/shared-costs").data?.doc.categories ?? [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const dirty = JSON.stringify(rows) !== JSON.stringify(data.categories);
  const tops = rows.filter((c) => !c.parentId);
  const used = new Set(data.accounts.map((a) => a.own).filter(Boolean));
  const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30) || "category";
  const newId = (name: string) => {
    let id = slug(name);
    for (let n = 2; rows.some((r) => r.id === id); n++) id = `${slug(name)}-${n}`;
    return id;
  };
  const set = (id: string, patch: Partial<SpendCategory>) => setRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/books/categories", { etag: data.etag, categories: rows });
      reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>Spending categories</Subtitle2>
        <div className="row-actions">
          <Button size="small" onClick={() => setRows([...rows, { id: newId("new category"), name: "New category" }])}>
            Add a category
          </Button>
          <Button appearance="primary" size="small" disabled={!dirty || busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
      <Caption1>
        Two levels: a category and its subcategories. People's subcategories drill into each partner, employee, contractor or benefit in the reports. A category with accounts in it can't be removed until they move. <strong>Shared cost</strong> says
        which shared-cost category its company spending feeds: once one is set, each past month QuickBooks has read replaces the actuals entered on Shared costs, while budgets still drive the forecast. A subcategory feeds its parent's.
      </Caption1>
      <ErrorBar message={error} />
      <table className="edit-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Within</th>
            <th>Shared cost</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {tops.flatMap((top) => [top, ...rows.filter((r) => r.parentId === top.id)]).map((c) => (
            <tr key={c.id}>
              <td className={c.parentId ? "indent" : undefined}>
                <Input size="small" value={c.name} disabled={c.id === UNMAPPED} onChange={(_, v) => set(c.id, { name: v.value })} aria-label="Category name" />
                {c.people && <Caption1 className="block muted">Drills into each {c.people === "benefits" ? "benefit" : c.people.replace(/s$/, "")}</Caption1>}
              </td>
              <td>
                {c.id === UNMAPPED || rows.some((r) => r.parentId === c.id) ? (
                  <Caption1 className="muted">{c.parentId ? categoryLabel(c.parentId, rows) : "Top level"}</Caption1>
                ) : (
                  <Select size="small" value={c.parentId ?? ""} aria-label="Within" onChange={(_, v) => set(c.id, { parentId: v.value || undefined })}>
                    <option value="">Top level</option>
                    {tops
                      .filter((t) => t.id !== c.id && t.id !== UNMAPPED)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </Select>
                )}
              </td>
              <td>
                {c.parentId || c.id === UNMAPPED || c.id === "people" ? (
                  <Caption1 className="muted">{c.id === "people" ? "People are never shared costs" : c.parentId ? "As its parent" : "—"}</Caption1>
                ) : (
                  <Select size="small" value={c.shared ?? ""} aria-label="Shared cost" onChange={(_, v) => set(c.id, { shared: v.value || undefined })}>
                    <option value="">Not a shared cost</option>
                    {shared
                      .filter((s) => s.active || s.id === c.shared)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                  </Select>
                )}
              </td>
              <td>
                {!c.parentId && c.id !== UNMAPPED && (
                  <Button size="small" appearance="subtle" onClick={() => setRows([...rows, { id: newId(`${c.id} sub`), name: "New subcategory", parentId: c.id }])}>
                    Add a subcategory
                  </Button>
                )}
                {c.id !== UNMAPPED && !used.has(c.id) && !rows.some((r) => r.parentId === c.id) && (
                  <Button size="small" appearance="subtle" onClick={() => setRows(rows.filter((r) => r.id !== c.id))}>
                    Remove
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// --- Assign (QB8) -----------------------------------------------------------------------------------------------

function Assign({ onChange }: { onChange: () => void }) {
  const data = useApi<AssignResponse>("/books/assign");
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const d = data.data;
  if (!d) return data.error ? <ErrorBar message={data.error} /> : <Loading />;
  const assign = async (line: ResolvedLine, value: string | null) => {
    setBusy(line.key);
    setError(undefined);
    try {
      await send("PUT", "/books/assign", { key: line.key, ...(value ? (line.kind === "income" ? { clientName: value } : { categoryId: value }) : {}) });
      data.reload();
      onChange();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(undefined);
    }
  };
  const table = (rows: ResolvedLine[], assigned: boolean) => (
    <div className="table-scroll">
      <table className="edit-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Transaction</th>
            <th>Payee or payer</th>
            <th>Account</th>
            <th>Memo</th>
            <th className="num">Amount</th>
            <th>{assigned ? "Assigned" : "Client or category"}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.key}>
              <td>{day(l.date)}</td>
              <td>
                {l.txnType} {l.docNumber ?? ""}
              </td>
              <td>{l.partyName ?? ""}</td>
              <td>{l.accountName}</td>
              <td>{l.memo ?? ""}</td>
              <td className="num">{money(l.amount)}</td>
              <td>
                {assigned ? (
                  <span className="inline-field">
                    {l.kind === "income" ? l.clientName : categoryLabel(l.categoryId ?? "", d.categories)}
                    <Button size="small" appearance="subtle" disabled={busy === l.key} onClick={() => void assign(l, null)}>
                      Clear
                    </Button>
                  </span>
                ) : (
                  <span className="inline-field">
                    {l.kind === "income" ? (
                      <Input size="small" list="assign-clients" value={picks[l.key] ?? ""} placeholder="Client" onChange={(_, v) => setPicks({ ...picks, [l.key]: v.value })} aria-label="Client" />
                    ) : (
                      <CategorySelect value={picks[l.key] ?? ""} categories={d.categories} inherit="Pick a category" exclude={UNMAPPED} onChange={(v) => setPicks({ ...picks, [l.key]: v })} />
                    )}
                    <Button size="small" disabled={!picks[l.key]?.trim() || busy === l.key} onClick={() => void assign(l, picks[l.key].trim())}>
                      Assign
                    </Button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <>
      <ErrorBar message={error} />
      <Card className="panel wide">
        <Subtitle2>Waiting for a client or category</Subtitle2>
        <Caption1>
          Income QuickBooks has no customer for, and spending in an account with no category. Until assigned they show in the reports as Not yet assigned or Unmapped, so nothing drops out. An assignment stays in Base Camp and never changes QuickBooks; if
          the line is later fixed in QuickBooks, QuickBooks's answer wins. For a whole account, set its category on the Accounts tab instead.
        </Caption1>
        {d.open.length === 0 ? <Body1 className="muted">Nothing waiting.</Body1> : table(d.open, false)}
        <datalist id="assign-clients">
          {d.knownClients.map((k) => (
            <option key={k} value={k} />
          ))}
        </datalist>
      </Card>
      {d.assigned.length > 0 && (
        <Card className="panel wide">
          <Subtitle2>Assigned in Base Camp</Subtitle2>
          {table(d.assigned, true)}
        </Card>
      )}
    </>
  );
}
